import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { InkPad } from '../../features/ink/InkPad';
import {
  checkSupabasePracticeMathAnswer,
  createSupabaseTodayMathAssignment,
  getSupabaseRuntimeIdentity,
  submitSupabaseTrustedAttempt,
  touchSupabaseLearningSession,
  uploadSupabaseInkDocument,
} from '../../features/runtime/supabaseAdapter';
import type { CloudAssignmentManifestV1, RuntimeIdentityV1 } from '../../features/runtime/types';
import type { TrustedAttemptSubmissionV1 } from '../../features/runtime/trustedRuntime';
import { getSupabaseClient } from '../../lib/supabase';
import { ActivityTimer } from '../../lib/learning/activityTimer';
import type { InkDocumentV1 } from '../../types/ink';
import { formatDuration } from '../../features/pilotMath/format';

function promptText(prompt: unknown) {
  if (typeof prompt === 'string') return { text: prompt, supportingText: '' };
  if (prompt && typeof prompt === 'object') {
    const row = prompt as Record<string, unknown>;
    return {
      text: typeof row.text === 'string' ? row.text : typeof row.prompt === 'string' ? row.prompt : 'Soal BelajarKu',
      supportingText: typeof row.supportingText === 'string' ? row.supportingText : '',
    };
  }
  return { text: 'Soal BelajarKu', supportingText: '' };
}

function difficultyBand(value: unknown) {
  if (!value || typeof value !== 'object') return '';
  const band = (value as Record<string, unknown>).band;
  return typeof band === 'string' ? band : '';
}

function reasonCode(value: unknown) {
  if (!value || typeof value !== 'object') return '';
  const reason = (value as Record<string, unknown>).reasonCode;
  if (reason === 'remediation') return 'Penguatan';
  if (reason === 'mastery_check') return 'Cek penguasaan';
  if (reason === 'spaced_review') return 'Review';
  return 'Latihan saat ini';
}

async function answeredQuestionIds(manifest: CloudAssignmentManifestV1, studentId: string) {
  const client = getSupabaseClient();
  if (!client) return new Set<string>();
  const itemIds = [...new Set(manifest.questions.map((item) => item.assignmentItemId))];
  if (!itemIds.length) return new Set<string>();
  const attempts = await client.from('attempts').select('id').eq('student_id', studentId).in('assignment_item_id', itemIds);
  if (attempts.error) throw attempts.error;
  const attemptIds = (attempts.data ?? []).map((row) => String(row.id));
  if (!attemptIds.length) return new Set<string>();
  const answers = await client.from('attempt_answers').select('question_instance_id').in('attempt_id', attemptIds);
  if (answers.error) throw answers.error;
  return new Set((answers.data ?? []).map((row) => String(row.question_instance_id)));
}

export function CloudTodayMathPage() {
  const [identity, setIdentity] = useState<RuntimeIdentityV1 | null>(null);
  const [manifest, setManifest] = useState<CloudAssignmentManifestV1 | null>(null);
  const [answered, setAnswered] = useState<Set<string>>(new Set());
  const [index, setIndex] = useState(0);
  const [answer, setAnswer] = useState('');
  const [firstResponse, setFirstResponse] = useState<number | null>(null);
  const [correctionCount, setCorrectionCount] = useState(0);
  const [hintCount, setHintCount] = useState(0);
  const [feedback, setFeedback] = useState<'idle' | 'checking' | 'incorrect' | 'correct' | 'error'>('idle');
  const [message, setMessage] = useState('');
  const [ink, setInk] = useState<InkDocumentV1>({ version: 1, width: 700, height: 220, strokes: [] });
  const [lastMastery, setLastMastery] = useState<{ skillCode: string; masteryPercent: number; state: string } | null>(null);
  const timer = useRef<ActivityTimer | null>(null);
  const questionStartedAt = useRef(new Date().toISOString());
  const clientSessionId = useRef(`cloud-session-${crypto.randomUUID()}`);

  const question = manifest?.questions[index];
  const progress = useMemo(() => manifest ? `${answered.size}/${manifest.questions.length}` : '0/0', [answered.size, manifest]);
  const onInkChange = useCallback((document: InkDocumentV1) => setInk(document), []);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const currentIdentity = await getSupabaseRuntimeIdentity();
        if (!currentIdentity?.student) throw new Error('Student Record belum tersedia.');
        const currentManifest = await createSupabaseTodayMathAssignment();
        const completed = await answeredQuestionIds(currentManifest, currentIdentity.student.id);
        if (cancelled) return;
        setIdentity(currentIdentity);
        setManifest(currentManifest);
        setAnswered(completed);
        const next = currentManifest.questions.findIndex((item) => !completed.has(item.questionInstanceId));
        setIndex(next >= 0 ? next : currentManifest.questions.length);
      } catch (error) {
        if (!cancelled) { setFeedback('error'); setMessage(error instanceof Error ? error.message : 'Tugas cloud tidak dapat dimuat.'); }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!question || !manifest) return;
    timer.current = new ActivityTimer();
    questionStartedAt.current = new Date().toISOString();
    setAnswer(''); setFirstResponse(null); setCorrectionCount(0); setHintCount(0); setFeedback('idle'); setMessage('');
    setInk({ version: 1, width: 700, height: 220, strokes: [] });
    void touchSupabaseLearningSession({
      clientSessionId: clientSessionId.current,
      assignmentId: manifest.assignment.id,
      questionInstanceId: question.questionInstanceId,
      status: 'active', elapsedDurationMs: 0, activeDurationMs: 0, pausedDurationMs: 0,
    }).catch(() => undefined);
  }, [manifest, question?.questionInstanceId]);

  async function finishQuestion(value: number) {
    if (!manifest || !question || !identity?.student) return;
    const duration = timer.current?.snapshot() ?? { elapsedDurationMs: 0, activeDurationMs: 0, pausedDurationMs: 0 };
    const finalFirst = firstResponse ?? value;
    const completedAt = new Date().toISOString();
    const payload: TrustedAttemptSubmissionV1 = {
      schemaVersion: 1,
      clientSubmissionId: `app-${crypto.randomUUID()}`,
      studentRef: identity.student.id,
      assignmentRef: manifest.assignment.id,
      questionRef: question.questionInstanceId,
      skillCode: question.skill.code ?? '',
      response: { type: 'integer', value },
      firstResponse: { type: 'integer', value: finalFirst },
      correctionCount,
      hintCount,
      timing: duration,
      startedAt: questionStartedAt.current,
      completedAt,
      ...(ink.strokes.length ? {
        ink: {
          present: true as const, formatVersion: 1 as const, strokeCount: ink.strokes.length,
          pointCount: ink.strokes.reduce((sum, stroke) => sum + stroke.points.length, 0),
        },
      } : {}),
    };
    const result = await submitSupabaseTrustedAttempt(payload);
    const attemptAnswerId = result.artifacts?.attemptAnswerId;
    if (attemptAnswerId && ink.strokes.length) {
      await uploadSupabaseInkDocument({ studentId: identity.student.id, attemptAnswerId, document: ink });
    }
    if (result.learnerUpdate) {
      setLastMastery({ skillCode: result.learnerUpdate.skillCode, masteryPercent: result.learnerUpdate.masteryPercent, state: result.learnerUpdate.masteryState });
    }
    const nextAnswered = new Set(answered); nextAnswered.add(question.questionInstanceId); setAnswered(nextAnswered);
    const nextIndex = manifest.questions.findIndex((item, candidateIndex) => candidateIndex > index && !nextAnswered.has(item.questionInstanceId));
    setFeedback('correct');
    window.setTimeout(() => setIndex(nextIndex >= 0 ? nextIndex : manifest.questions.length), 420);
  }

  async function submitAnswer() {
    if (!manifest || !question || feedback === 'checking') return;
    const value = Number(answer);
    if (!Number.isInteger(value)) return;
    setFeedback('checking'); setMessage('Memeriksa di server…');
    try {
      const checked = await checkSupabasePracticeMathAnswer({ assignmentId: manifest.assignment.id, questionInstanceId: question.questionInstanceId, value });
      if (!checked.correct) {
        if (firstResponse === null) setFirstResponse(value);
        setCorrectionCount((current) => current + 1);
        setHintCount((current) => current + 1);
        setFeedback('incorrect');
        setMessage('Belum tepat. Perhatikan nilai tempat, lalu coba lagi.');
        setAnswer('');
        return;
      }
      await finishQuestion(value);
      setMessage('Benar. Evidence dan mastery diperbarui oleh server.');
    } catch (error) {
      setFeedback('error'); setMessage(error instanceof Error ? error.message : 'Jawaban tidak dapat diproses.');
    }
  }

  if (!manifest || !identity) {
    return <section className="card">{feedback === 'error' ? <div className="feedback feedback--error">{message}</div> : 'Menyiapkan tugas Matematika dari cloud…'}</section>;
  }

  if (!question || index >= manifest.questions.length) {
    return (
      <div className="stack">
        <section className="card hero-card">
          <div className="eyebrow">Belajar hari ini selesai</div>
          <h1>Kerja bagus, {identity.student?.displayName}!</h1>
          <p className="muted">{manifest.questions.length} frozen question sudah tercatat pada Student Record.</p>
        </section>
        {lastMastery && <section className="card"><strong>{lastMastery.skillCode}</strong><p>Mastery terakhir: {lastMastery.masteryPercent}% · {lastMastery.state.replaceAll('_', ' ')}</p></section>}
        <div className="button-row"><Link className="btn btn--primary" to="/student/progress">Lihat Progress</Link><Link className="btn" to={`/print/assignment/${manifest.assignment.id}`}>Worksheet / QR</Link></div>
      </div>
    );
  }

  const prompt = promptText(question.prompt);
  const duration = timer.current?.snapshot();
  return (
    <div className="stack today-math">
      <header className="learning-header">
        <div><div className="eyebrow">Matematika · Cloud Daily Learning</div><h1>{question.skill.label ?? question.skill.code}</h1><p className="muted">{reasonCode(question.difficulty)} · {difficultyBand(question.difficulty)}</p></div>
        <div className="learning-progress"><strong>{index + 1}/{manifest.questions.length}</strong><div className="progress-track"><span style={{ width: `${(answered.size / manifest.questions.length) * 100}%` }} /></div></div>
      </header>
      <div className="button-row">
        <Link className="btn btn--primary" to={`/print/assignment/${manifest.assignment.id}`}>Cetak Worksheet / QR</Link>
        <Link className="btn" to="/student/progress">Lihat Progress</Link>
      </div>
      <section className="card question-card">
        <div className="question-meta"><span className="pill">{reasonCode(question.difficulty)}</span><span className="muted">{question.skill.code}</span></div>
        <div className="cloud-question-prompt">{prompt.text}</div>
        {prompt.supportingText && <div className="cloud-question-support muted">{prompt.supportingText}</div>}
        <InkPad onChange={onInkChange} />
        <div className="answer-panel">
          <label htmlFor="cloud-structured-answer"><strong>Jawaban untuk diperiksa</strong></label>
          <div className="answer-row"><input id="cloud-structured-answer" className="answer-input" inputMode="numeric" value={answer} onChange={(event) => setAnswer(event.target.value.replace(/[^0-9-]/g, ''))} onKeyDown={(event) => { if (event.key === 'Enter') void submitAnswer(); }} /><button className="btn btn--primary btn--large" type="button" disabled={!answer.trim() || feedback === 'checking'} onClick={() => void submitAnswer()}>{feedback === 'checking' ? 'Memeriksa…' : 'Periksa'}</button></div>
          <p className="muted">Correctness diperiksa server tanpa mengirim answer key ke browser. Ink di-upload setelah authoritative submission.</p>
        </div>
        {feedback === 'incorrect' && <div className="feedback feedback--error"><strong>Belum tepat.</strong><p>{message}</p></div>}
        {feedback === 'correct' && <div className="feedback"><strong>Benar.</strong><p>{message}</p></div>}
        {feedback === 'error' && <div className="feedback feedback--error"><strong>Belum dapat diproses.</strong><p>{message}</p></div>}
      </section>
      <section className="learning-footer muted cloud-sync-status"><span>Selesai: {progress}</span><span>Koreksi soal ini: {correctionCount}</span><span>Hint: {hintCount}</span>{duration && <span>Active: {formatDuration(duration.activeDurationMs)}</span>}</section>
    </div>
  );
}
